<template>
  <BaseModal
    :value="true"
    :modal-title="$t('utils.notificationHistory')"
    @close="$emit('close')"
  >
    <template #modal>
      <div class="modal_content">
        <div v-if="loading" class="loading_state">
          <BaseProgressCircular color="primary" size="40" indeterminate />
        </div>

        <template v-else-if="folderReminds.length">
          <div class="reminders_feed">
            <div
              v-for="remind in folderReminds"
              :key="remind.id"
              class="reminder_card"
            >
              <div class="email_header">
                <div class="email_meta">
                  <div class="meta_row">
                    <span class="meta_label">{{
                      $t("utils.emailAction.from")
                    }}</span>
                    <div class="recipients">
                      <span
                        v-if="remind.collaborator && remind.collaborator.email"
                        class="recipient_chip is_neutral"
                        :title="remind.collaborator.email"
                      >
                        <BaseIcon icon="$mdiEmailOutline" :size="12" />
                        {{ remind.collaborator.email }}
                      </span>
                      <span v-else class="meta_value">—</span>
                    </div>
                  </div>
                  <div class="meta_row">
                    <span class="meta_label">{{
                      $t("utils.emailAction.to")
                    }}</span>
                    <div class="recipients">
                      <span
                        v-if="remind.email"
                        class="recipient_chip is_email"
                        :title="remind.email"
                      >
                        <BaseIcon icon="$mdiEmailOutline" :size="12" />
                        {{ remind.email }}
                      </span>
                      <span
                        v-if="remind.phoneNumber"
                        class="recipient_chip is_sms"
                        :title="remind.phoneNumber"
                      >
                        <BaseIcon icon="$mdiMessageText" :size="12" />
                        {{ remind.phoneNumber }}
                      </span>
                      <span
                        v-if="!remind.email && !remind.phoneNumber"
                        class="meta_value"
                      >
                        —
                      </span>
                    </div>
                  </div>
                </div>
                <div class="email_side">
                  <span
                    class="status_chip"
                    :class="statusMeta(remind.status).class"
                  >
                    <BaseIcon :icon="statusMeta(remind.status).icon" :size="12" />
                    {{ $t(statusMeta(remind.status).labelKey) }}
                  </span>
                  <div class="email_date">
                    {{ $d(new Date(remind.dateCreated), "detailed") }}
                  </div>
                </div>
              </div>

              <div class="email_body">
                <BaseMarkdownViewer
                  v-if="remind.transactionalEmail"
                  :content="remind.transactionalEmail.emailContent"
                  class="email_content"
                />
                <span v-else-if="remind.content" class="email_content plain">
                  {{ remind.content }}
                </span>
                <span v-else class="text_empty">—</span>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="empty_state">
          <BaseIcon icon="$mdiBellOutline" :size="36" color="grey lighten-2" />
          <span class="empty_label">
            {{ $t("FOLDER_NOTIFICATION_HISTORY_DIALOG.LABEL.EMPTY") }}
          </span>
        </div>
      </div>
    </template>
    <template #actions>
      <BaseButton text class="ml-auto" color="#707080" @click="$emit('close')">
        {{ $t("navigation.close") }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { getFolderRemind } from "@/modules/Folder/Services/folder.service";
import { REMINDER_STATUS } from "@/core/Models/Remind";
import BaseMarkdownViewer from "@/core/Components/UI/Markdown/BaseMarkdownViewer.vue";

const STATUS_META = {
  [REMINDER_STATUS.PENDING]: {
    icon: "$mdiClockOutline",
    labelKey: "utils.emailAction.pending",
    class: "is_pending",
  },
  [REMINDER_STATUS.IN_PROGRESS]: {
    icon: "$mdiProgressClock",
    labelKey: "utils.emailAction.inProgess",
    class: "is_active",
  },
  [REMINDER_STATUS.SENT]: {
    icon: "$mdiSendOutline",
    labelKey: "utils.emailAction.emailSent",
    class: "is_active",
  },
  [REMINDER_STATUS.SUCCESS]: {
    icon: "$mdiCheckCircleOutline",
    labelKey: "utils.emailAction.success",
    class: "is_positive",
  },
  [REMINDER_STATUS.OPEN]: {
    icon: "$mdiEmailOpenOutline",
    labelKey: "utils.emailAction.open",
    class: "is_positive",
  },
  [REMINDER_STATUS.CLICKED]: {
    icon: "$mdiCursorDefaultClickOutline",
    labelKey: "utils.emailAction.clicked",
    class: "is_positive",
  },
  [REMINDER_STATUS.ERROR]: {
    icon: "$mdiAlertCircleOutline",
    labelKey: "utils.emailAction.error",
    class: "is_negative",
  },
  [REMINDER_STATUS.SPAM]: {
    icon: "$mdiAlertOctagonOutline",
    labelKey: "utils.emailAction.spam",
    class: "is_negative",
  },
};

export default {
  name: "FolderNotificationHistoryDialog",
  components: { BaseMarkdownViewer },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    folderId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      folderReminds: [],
      loading: false,
    };
  },
  created() {
    this.fetchFolderReminds();
  },
  methods: {
    async fetchFolderReminds() {
      this.loading = true;
      try {
        this.folderReminds = await getFolderRemind(
          this.organizationId,
          this.folderId
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.loading = false;
    },
    statusMeta(status) {
      return STATUS_META[status] ?? STATUS_META[REMINDER_STATUS.PENDING];
    },
  },
};
</script>
<style lang="scss" scoped>
.modal_content {
  max-height: 65vh;
  overflow-y: auto;
}

// ── Loading ───────────────────────────────────────────────────────────────────

.loading_state {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

// ── Feed ──────────────────────────────────────────────────────────────────────

.reminders_feed {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reminder_card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  font-family: "Inter", sans-serif;
}

// ── Email header ──────────────────────────────────────────────────────────────

.email_header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.email_meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.meta_row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta_label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  width: 22px;
  flex: none;
}

.meta_value {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ── Recipient chips ───────────────────────────────────────────────────────────

.recipients {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.recipient_chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.is_neutral {
    background: #f8fafc;
    color: #64748b;
  }

  &.is_email {
    background: #eef1ff;
    color: #2536cc;
  }

  &.is_sms {
    background: #fff7ed;
    color: #ea580c;
  }
}

// ── Status chip ───────────────────────────────────────────────────────────────

.email_side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex: none;
}

.status_chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.is_pending {
    background: #f1f5f9;
    color: #64748b;
  }

  &.is_active {
    background: #eef1ff;
    color: #2536cc;
  }

  &.is_positive {
    background: #ecfdf5;
    color: #059669;
  }

  &.is_negative {
    background: #fef2f2;
    color: #dc2626;
  }
}

.email_date {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
  flex: none;
  padding-top: 2px;
}

// ── Email body ────────────────────────────────────────────────────────────────

.email_body {
  padding: 16px;
  background: white;
  max-height: 220px;
  overflow-y: auto;
}

.email_content {
  font-size: 13px;
  line-height: 1.6;
  color: #334155;
  word-break: break-word;

  &.plain {
    white-space: pre-wrap;
  }

  ::v-deep p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ::v-deep a {
    color: #2536cc;
  }
}

.text_empty {
  font-size: 13px;
  color: #94a3b8;
}

// ── Empty state ───────────────────────────────────────────────────────────────

.empty_state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.empty_label {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 8px;
}
</style>
